import { CmsAccordionProps } from '@chewy/cms-accordion/src/typings';
import { CmsAccordionItemData } from '@chewy/cms-accordion/src/typings';
import { CmsBannerProps } from '@chewy/cms-banner/src/typings';
import {
  CmsBannerCoreHeadings,
  CmsBannerCoreHeights,
  CmsBannerCoreStyles,
} from '@chewy/cms-banner/src/banner-core/typings';
import {
  CmsBannerCtaType,
  CmsBannerVerticalAlignment,
} from '@chewy/cms-banner/src/typings';
import { CmsContentBlockProps } from '@chewy/cms-content-block/src/typings';
import {
  CmsContentBlockLayoutType,
  CmsContentBlockPaddingType,
  CmsContentBlockVerticalAlignment,
  ICmsContentBlockItem,
} from '@chewy/cms-content-block/src/typings';
import { CmsEducationalProps } from '@chewy/cms-educational';
import {
  CmsHpHeroCarouselBanner,
  CmsHpHeroCarouselProps,
} from '@chewy/cms-hp-hero-carousel/src/typings';
import {
  CmsNonSkuProps,
  CmsNonSkuImageCrop,
  CmsNonSkuItem,
  CmsNonSkuLayoutType,
  CmsNonSkuOrientationMobile,
  CmsNonSkuRecipeType,
  CmsNonSkuShapeDescription,
  CmsNonSkuSubTitleOptions,
  CmsNonSkuTileTitleTextSize,
} from '@chewy/cms-non-sku/src/typings';
import {
  Analytics,
  Header,
  Content,
  Link,
  Images,
  Alignment,
  SubText,
} from '@chewy/cms-shared-typings';
import { CmsSitewideBannerProps } from '@chewy/cms-sitewide-banner/src/typings';
import {
  CmsSkuProps,
  CmsSkuBadgeTypes,
  CmsSkuItemType,
  CmsSkuOrientationTypes,
  CmsSkuPlacementTypes,
  CmsSkuPriceTypes,
  CmsSkuRecipeTypes,
  CmsSkuTitleDetailsType,
} from '@chewy/cms-sku/src/typings';

export type CmsComponentData =
  | CmsAccordionData
  | CmsBannerData
  | CmsContentBlockData
  | CmsEducationalData
  | CmsHpHeroCarouselData
  | CmsNonSkuData
  | CmsSitewideBannerData
  | CmsSkuData;

export interface CmsUniversalProps extends React.HTMLAttributes<HTMLElement> {
  data: CmsComponentData;
  options?: {
    accordion?: CmsAccordionProps;
    banner?: CmsBannerProps;
    contentBlock?: CmsContentBlockProps;
    educational?: CmsEducationalProps;
    hpHeroCarousel?: CmsHpHeroCarouselProps;
    nonSku?: CmsNonSkuProps;
    sitewideBanner?: CmsSitewideBannerProps;
    sku?: CmsSkuProps;
  };
}

export enum CmsDataType {
  'Accordion' = 'ACCORDION',
  'Banner' = 'STANDARD-BANNER',
  'BannerCarousel' = 'STANDARD-BANNER-CAROUSEL',
  'ContentBlock' = 'STANDARD-CONTENT-BLOCK',
  'Card' = 'STANDARD-CARD',
  'NonSku' = 'STANDARD-NON-SKU',
  'SiteWideBanner' = 'STANDARD-SITE-WIDE-BANNER',
  'Sku' = 'STANDARD-SKU',
}

export interface AnalyticsInstrumentation {
  [key: string]: string | number | boolean | null;
}

export interface CmsData {
  id: string;
  type: CmsDataType;
  header: Header | null;
  analytics: Analytics;

  instrumentation: AnalyticsInstrumentation;
}

export interface CmsAccordionData extends CmsData {
  type: CmsDataType.Accordion;
  header: Header;
  placement: string | null;
  items: CmsAccordionItemData[];
}

export interface CmsBannerData extends CmsData {
  type: CmsDataType.Banner;
  accLabel: string;
  altText: string;
  assetToggle: boolean;
  bannerType: string;
  contentAlignment: Alignment; // deprecated
  contentHorizontalAlignment: Alignment;
  contentVerticalAlignment: CmsBannerVerticalAlignment;
  textAlignment: Alignment;
  ctas: CmsBannerCtaType[] | [];
  headerTag: CmsBannerCoreHeadings;
  height: CmsBannerCoreHeights;
  link: Link;
  images: Images;
  subText: Content | null;
  topLine: Content | null;
  textStyles: CmsBannerCoreStyles | null;
}

export interface CmsHpHeroCarouselData extends CmsData {
  type: CmsDataType.BannerCarousel;
  items: CmsHpHeroCarouselBanner[];
  slideInterval: number;
}

export interface CmsContentBlockData extends CmsData {
  type: CmsDataType.ContentBlock;
  header: Header;
  backgroundColor: string;
  layoutType: CmsContentBlockLayoutType;
  marginBottom: CmsContentBlockPaddingType;
  marginTop: CmsContentBlockPaddingType;
  numberOfColumns: number;
  verticalAlignment: CmsContentBlockVerticalAlignment;
  items: ICmsContentBlockItem[];
}

export interface CmsEducationalData extends CmsData {
  type: CmsDataType.Card;
  backgroundColor: string;
  body: {
    alignment: Alignment | null | string;
    richText: string | null;
    text: string | null;
    textColor: string | null;
    textStyle: string | null;
  };
  header: never;
  headline: {
    alignment: Alignment | null | string;
    richText: string;
    text: string | null;
    textColor: string | null;
    textStyle: string;
  };
  images: Images;
  link: Link;
  source: {
    alignment: Alignment | null | string;
    richText: string | null;
    text: string | null;
    textColor: string | null;
    textStyle: string | null;
  };
}

export interface CmsNonSkuData extends CmsData {
  type: CmsDataType.NonSku;
  backgroundColor: string | null;
  constraints: {
    type: string;
    status: boolean;
    value: string;
  }[];
  header: Header;
  imageCrop: CmsNonSkuImageCrop;
  imageVeil: boolean;
  layoutType: CmsNonSkuLayoutType;
  link: Link;
  maxItems: number;
  minItems: number;
  orientationMobile: CmsNonSkuOrientationMobile;
  recipeType: CmsNonSkuRecipeType;
  shapeDescription: CmsNonSkuShapeDescription;
  subText: SubText;
  subTitleLink: Link;
  subTitleOptions: CmsNonSkuSubTitleOptions;
  tileTitleTextSize: CmsNonSkuTileTitleTextSize;
  items: CmsNonSkuItem[];
}
export interface CmsSitewideBannerData extends CmsData {
  type: CmsDataType.SiteWideBanner;
  asset: {
    pdfDarkUrl: string;
    pdfLightUrl: string;
    svgMarkup: string;
    source: string;
    voiceOverText: string;
  };
  assetPlacement: string;
  bodyText: string;
  bodyTextColor: string;
  ctaOptions: string;
  header: never;
  modalImages: Images;
  modalImageAltText: string;
  newPageUrl: string;
  footnote: string;
}

export interface CmsSkuData extends CmsData {
  type: CmsDataType.Sku;
  subType: string;
  backgroundColor: null;
  header: Header;
  badges: CmsSkuBadgeTypes[];
  imageOrientation: CmsSkuOrientationTypes;
  items: CmsSkuItemType[];
  link: Link;
  placementType: CmsSkuPlacementTypes;
  prices: CmsSkuPriceTypes[];
  recipeType: CmsSkuRecipeTypes;
  showAddToCartButton: boolean;
  showBundleLabel: boolean;
  showNumberOfReviews: boolean;
  showProductDescription: boolean;
  showStars: boolean;
  skuTypeOption: string;
  titleDetails: CmsSkuTitleDetailsType[];
  constraints: {
    type: string;
    status: boolean;
    value: string;
  }[];
}
